import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { ChevronLeft, ChevronRight, Download, Keyboard } from 'lucide-react';
import { Button } from './Button.js';
import { cn } from './cn.js';

export interface Column<T> {
  key: string;
  header: string;
  width?: string;
  render: (row: T) => React.ReactNode;
  className?: string;
}

/**
 * The universal queue (docs/03 §26.1): server-side paging (NFR-18), keyboard
 * triage (j/k/Enter + configurable action keys, ? for the map), CSV export of
 * the current page respecting active filters (AC-ADMGL-11), and an explicit
 * empty state. Rows are virtualised by the browser via simple windowing —
 * pages are ≤50 so a plain table is fastest.
 */
export function Queue<T>({
  rows,
  columns,
  rowKey,
  onOpen,
  actions,
  nextCursor,
  prevCursor,
  onPage,
  total,
  loading,
  empty,
  csvName,
  filters,
  title,
}: {
  rows: T[];
  columns: Column<T>[];
  rowKey: (r: T) => string;
  onOpen: (r: T) => void;
  /** Keyboard actions: key → handler(row). Shown in the ? map. */
  actions?: Array<{
    key: string;
    label: string;
    run: (r: T) => void;
    disabled?: (r: T) => boolean;
  }>;
  nextCursor: string | null;
  prevCursor: string | null;
  onPage: (c: string | null) => void;
  total?: number;
  loading?: boolean;
  empty: string;
  csvName: string;
  filters?: React.ReactNode;
  title: string;
}) {
  const [cursor, setCursor] = useState(0);
  const [showMap, setShowMap] = useState(false);
  const tableRef = useRef<HTMLTableElement>(null);
  // A new result set puts the keyboard cursor back on the first row. Adjusting
  // during render (react.dev "You Might Not Need an Effect") avoids the extra
  // commit an effect would cause on every refetch of a moderation queue.
  const [rowsSeen, setRowsSeen] = useState(rows);
  if (rowsSeen !== rows) {
    setRowsSeen(rows);
    setCursor(0);
  }
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement;
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(t.tagName) || t.isContentEditable) return;
      if (e.key === '?') {
        setShowMap((s) => !s);
        return;
      }
      if (rows.length === 0) return;
      if (e.key === 'j' || e.key === 'ArrowDown') {
        e.preventDefault();
        setCursor((c) => Math.min(rows.length - 1, c + 1));
      } else if (e.key === 'k' || e.key === 'ArrowUp') {
        e.preventDefault();
        setCursor((c) => Math.max(0, c - 1));
      } else if (e.key === 'Enter') {
        const r = rows[cursor];
        if (r) onOpen(r);
      } else {
        const a = actions?.find((x) => x.key === e.key);
        const r = rows[cursor];
        if (a && r && !a.disabled?.(r)) {
          e.preventDefault();
          a.run(r);
        }
      }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [rows, cursor, actions, onOpen]);
  useEffect(() => {
    tableRef.current
      ?.querySelector<HTMLElement>(`[data-row="${cursor}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [cursor]);

  const csv = useMemo(
    () => () => {
      const esc = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;
      const head = columns.map((c) => esc(c.header)).join(',');
      const body = rows.map((r) =>
        columns
          .map((c) => {
            const el = c.render(r);
            const txt =
              typeof el === 'string' || typeof el === 'number'
                ? el
                : (r as Record<string, unknown>)[c.key];
            return esc(txt);
          })
          .join(','),
      );
      const blob = new Blob([[head, ...body].join('\n')], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${csvName}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    },
    [rows, columns, csvName],
  );

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between gap-4 pb-3">
        <div className="flex items-baseline gap-3">
          <h1 className="text-lg font-semibold text-grey-900">{title}</h1>
          {total !== undefined && (
            <span className="text-sm text-grey-500">{total.toLocaleString()} total</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowMap((s) => !s)}
            title="Keyboard shortcuts (?)"
          >
            <Keyboard className="h-4 w-4" />
          </Button>
          <Button variant="secondary" size="sm" onClick={csv} disabled={rows.length === 0}>
            <Download className="h-4 w-4" /> CSV
          </Button>
        </div>
      </div>
      {filters && (
        <div className="mb-3 flex flex-wrap items-end gap-3 rounded-lg border bg-white px-4 py-3">
          {filters}
        </div>
      )}
      {showMap && (
        <div className="mb-3 flex flex-wrap gap-4 rounded-lg border bg-white px-4 py-3 text-xs">
          <span>
            <kbd className="kbd">j</kbd>/<kbd className="kbd">k</kbd> move
          </span>
          <span>
            <kbd className="kbd">Enter</kbd> open
          </span>
          {actions?.map((a) => (
            <span key={a.key}>
              <kbd className="kbd">{a.key}</kbd> {a.label}
            </span>
          ))}
          <span>
            <kbd className="kbd">?</kbd> this map
          </span>
        </div>
      )}
      <div
        className={cn(
          'min-h-0 flex-1 overflow-auto rounded-lg border bg-white',
          loading && 'opacity-60',
        )}
      >
        <table ref={tableRef} className="w-full border-collapse text-sm">
          <thead className="sticky top-0 z-10 bg-grey-100 text-left text-xs uppercase tracking-wide text-grey-500">
            <tr>
              {columns.map((c) => (
                <th
                  key={c.key}
                  className={cn('px-3 py-2 font-medium', c.className)}
                  style={c.width ? { width: c.width } : undefined}
                >
                  {c.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !loading && (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-3 py-12 text-center text-sm text-grey-500"
                >
                  {empty}
                </td>
              </tr>
            )}
            {rows.map((r, i) => (
              <tr
                key={rowKey(r)}
                data-row={i}
                onClick={() => {
                  setCursor(i);
                  onOpen(r);
                }}
                className={cn(
                  'cursor-pointer border-t hover:bg-blue-600/5',
                  i === cursor &&
                    'bg-blue-600/10 outline outline-1 -outline-offset-1 outline-blue-500',
                )}
              >
                {columns.map((c) => (
                  <td key={c.key} className={cn('px-3 py-2 align-middle', c.className)}>
                    {c.render(r)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between pt-3 text-xs text-grey-500">
        <span>
          Showing {rows.length} row{rows.length === 1 ? '' : 's'}
        </span>
        <div className="flex gap-1">
          <Button
            variant="secondary"
            size="sm"
            disabled={!prevCursor && prevCursor !== ''}
            onClick={() => onPage(prevCursor)}
          >
            <ChevronLeft className="h-4 w-4" /> Prev
          </Button>
          <Button
            variant="secondary"
            size="sm"
            disabled={!nextCursor}
            onClick={() => onPage(nextCursor)}
          >
            Next <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

const subscribeToMinute = (onTick: () => void) => {
  const id = setInterval(onTick, 60_000);
  return () => clearInterval(id);
};
const nowInMinutes = () => Math.floor(Date.now() / 60_000);

/**
 * SLA colouring (AC-ADM-20.2): green >12h, amber <12h, red breached. The clock
 * is an external mutable source, so it is read through `useSyncExternalStore`
 * with a per-minute snapshot — the badge re-colours as a deadline approaches
 * without a clock read during render.
 */
export function SlaBadge({ dueAt }: { dueAt: Date }) {
  const minute = useSyncExternalStore(subscribeToMinute, nowInMinutes, nowInMinutes);
  const ms = dueAt.getTime() - minute * 60_000;
  const h = ms / 3_600_000;
  const tone = ms <= 0 ? 'red' : h < 12 ? 'amber' : 'green';
  const label = ms <= 0 ? `Breached ${Math.abs(h).toFixed(0)}h ago` : `${h.toFixed(0)}h left`;
  const cls = {
    red: 'bg-red-600/10 text-red-600 border-red-600/30',
    amber: 'bg-gold-500/15 text-[#8a6d00] border-gold-500/40',
    green: 'bg-green-600/10 text-green-600 border-green-600/30',
  }[tone];
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-pill border px-2 py-0.5 text-xs font-medium tabular-nums',
        cls,
      )}
    >
      {label}
    </span>
  );
}

/** Cursor history for Prev/Next over server offsets. */
export function usePaging() {
  const [stack, setStack] = useState<string[]>([]);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  return {
    cursor,
    prevCursor: stack.length > 0 ? (stack[stack.length - 1] ?? '') : null,
    go: (next: string | null) => {
      if (next === null) return;
      if (stack.length > 0 && next === stack[stack.length - 1]) {
        setStack((s) => s.slice(0, -1));
        setCursor(next === '' ? undefined : next);
      } else {
        setStack((s) => [...s, cursor ?? '']);
        setCursor(next);
      }
    },
    reset: () => {
      setStack([]);
      setCursor(undefined);
    },
  };
}
